import { RawKpi } from './kpi.model';
import { PlanFinding } from './plan-finding.model';

/** A plan the assistant holds open in a live edit session, as one chat tab knows it. */
export interface AssistantPlanHold {
  tabId: string;
  editSessionId: string;
  planId: string;
  dataSpaceId: number;
}

export type PlanEditHolder = {
  kind: 'assistant';
  tabId: string;
  editSessionId: string;
  dataSpaceId: number;
};

/** The user-layer flavours GFE renders; a patch entity of any other flavour is ignored. */
export const RENDERED_FLAVOURS: readonly string[] = [
  'UserRoute',
  'UserJobTransportOrder',
  'UserWasteContainerJob'
];

export type PlanEditVia = 'assistant' | 'mcp' | 'app';

/** GBE keeps the socket's current watch when it refuses one for rate; the effects try again. */
export const PLAN_EDIT_RATE_LIMITED = 'plan_edit_rate_limited';

/** Who holds a plan's live edit session, as GBE's watch names them. */
export interface PlanWatchHolder {
  userId: number;
  name: string;
  via: PlanEditVia;
}

/** A whole user-layer entity, its keys snake_case as DL emits them. */
export interface WatchPatchUpsert {
  entityType: string;
  userEntityType: string;
  uid: string;
  data: Record<string, unknown>;
}

export interface WatchPatchRemoval {
  entityType: string;
  uid: string;
}

export interface WatchPatch {
  upserted: WatchPatchUpsert[];
  removed: WatchPatchRemoval[];
}

/** One state of the watched session, as a snapshot or a `watch-patch` brings it. */
export interface WatchPatchEvent {
  revision: number;
  recomputePending: boolean;
  patch: WatchPatch;
  findings: PlanFinding[] | null;
  /** The session's plan KPIs at this state; absent when GBE sent none, which keeps the last ones. */
  planKpis?: RawKpi[];
}

/** The session's unsaved changes relative to the plan as stored. */
export interface WatchOverlay {
  /** user_entity_type → uid → raw entity. */
  upserted: Record<string, Record<string, Record<string, unknown>>>;
  /** user_entity_type → entity_type (ROUTE, …), learned from upserts: removals name only the latter. */
  entityTypes: Record<string, string>;
  /** entity_type → uids the session removed. */
  removed: Record<string, string[]>;
}

/** Codes GBE and DL answer a refused edit command with; each has a translation. */
export const PLAN_EDIT_ERROR_CODES: readonly string[] = [
  'plan_edit_not_permitted',
  'plan_edit_plan_not_found',
  'plan_edit_session_already_open',
  'plan_edit_plan_locked',
  'plan_edit_optimization_running',
  'plan_edit_quota_exceeded',
  'plan_edit_resume_failed',
  'plan_edit_invalid_message',
  'plan_edit_no_session',
  'plan_edit_rate_limited',
  'plan_edit_watch_failed',
  'plan_edit_action_not_found',
  'plan_edit_block_action_not_found',
  'plan_edit_driver_not_found',
  'plan_edit_job_not_found',
  'plan_edit_route_not_found',
  'plan_edit_route_already_exists',
  'plan_edit_trailer_not_found',
  'plan_edit_vehicle_not_found',
  'plan_edit_action_time_before_predecessor',
  'plan_edit_job_precedence_broken',
  'plan_edit_moved_part_ambiguous',
  'plan_edit_moved_part_not_found',
  'plan_edit_operation_served_elsewhere',
  'plan_edit_not_a_permutation',
  'plan_edit_not_a_job_permutation',
  'plan_edit_site_mismatch',
  'plan_edit_action_serves_no_job',
  'plan_edit_anchor_inside_block',
  'plan_edit_block_includes_route_frame',
  'plan_edit_cannot_merge_into_trailer_exchange',
  'plan_edit_job_primary_part_not_on_route',
  'plan_edit_merged_stop_serves_two_reordered_jobs',
  'plan_edit_not_a_contiguous_block',
  'plan_edit_placement_before_route_start',
  'plan_edit_route_frozen_by_validation',
  'plan_edit_route_is_double_manned',
  'plan_edit_malformed_intent',
  'plan_edit_unknown_intent_type',
  'plan_edit_projection_failed',
  'plan_edit_projection_not_supported',
  'plan_edit_save_conflict',
  'plan_edit_already_open',
  'plan_edit_empty_block',
  'plan_edit_no_projection_requested',
  'plan_edit_open_required',
  'plan_edit_plan_deleted',
  'plan_edit_resume_unknown',
  'plan_edit_save_unsettled',
  'plan_edit_action_uids_exhausted',
  'plan_edit_company_not_found',
  'plan_edit_route_company_breached',
  'plan_edit_registration_not_of_resource',
  'plan_edit_route_serves_jobs',
  'plan_edit_route_is_template',
  'plan_edit_route_has_pre_assigned_jobs',
  'plan_edit_route_lacks_resource'
];

export const PLAN_EDIT_CLOSE_REASONS: readonly string[] = [
  'CLIENT',
  'IDLE_TIMEOUT',
  'SESSION_LIMIT',
  'BATCH_LIMIT',
  'JOURNAL_LIMIT',
  'OPEN_FAILED',
  'PROTOCOL_VIOLATION',
  'INTERNAL',
  'SUPERSEDED',
  'LOCK_LOST',
  'RATE_LIMITED',
  'MESSAGE_TOO_LARGE',
  'UPSTREAM_LOST',
  'SERVER_SHUTDOWN'
];
